import { z } from 'zod';
import { LoginResponseSchema } from './auth';

// Mirrors backend SignupIntent
export const SignupIntentSchema = z.enum(['personal', 'corporate']);
export type SignupIntent = z.infer<typeof SignupIntentSchema>;

// Mirrors backend CorporateOnboardingStatus. `provisioned` (CIQ-1591): a live
// sales-provisioned offer matches this verified email — render the claim step
// instead of the self-serve one; claiming now hands off straight to /chat
// (CIQ-2602 removed the Billing Setup / Invite Members steps that used to
// follow it). `trial_retired` and `poc_not_supported_for_usage` (CIQ-2431):
// the offer is one that redemption refuses.
// Keep `.catch`: a parse failure ejects the user to /login, so an unrecognised
// state must degrade to create_new rather than throw.
export const CorporateOnboardingStateSchema = z
    .enum(['join_existing', 'create_new', 'completed', 'provisioned', 'trial_retired', 'poc_not_supported_for_usage'])
    .catch('create_new');
export type CorporateOnboardingState = z.infer<typeof CorporateOnboardingStateSchema>;

export const CorporateOnboardingStatusResponseSchema = z.object({
    state: CorporateOnboardingStateSchema,
    workspace_id: z.number().optional(),
    workspace_name: z.string().optional(),
    // `provisioned` only: the offer's terms for the wizard's Step 1 and the
    // setup wizard's no-card copy.
    // `offer_workspace_name` is the salesperson's prefill; the signup form may
    // have collected an edit, which the wizard prefers when it has one.
    offer_kind: z.string().optional(),
    offer_workspace_name: z.string().optional(),
    trial_days: z.number().optional(),
    plan_name: z.string().optional(),
    sku_key: z.string().nullish(),
    plan_price_usd: z.number().nullish(),
    // CIQ-2502: the deal is collected by card, so the wizard takes one before
    // the workspace is created. Absent means invoice, which asks for nothing.
    requires_card: z.boolean().optional(),
    // CIQ-2502: what the payment step will take, present alongside requires_card.
    upfront_charge_usd: z.number().optional(),
    // The basis a `plan` offer was priced on. Set for any plan offer regardless
    // of requires_card -- an invoiced deal needs it on the assigned-plan card too.
    pricing_basis: z.string().nullish(),
    // CIQ-2431, `poc` offers only: the discount and its length.
    poc_discount_pct: z.number().optional(),
    poc_days: z.number().optional(),
});
export type CorporateOnboardingStatusResponse = z.infer<typeof CorporateOnboardingStatusResponseSchema>;

// GET /onboarding/corporate/offer-preview — the unauthenticated signup-landing
// prefills behind a ?offer= link (CIQ-1591). The email is LOCKED on the form;
// the workspace name is editable.
export const OfferPreviewResponseSchema = z.object({
    email: z.string(),
    workspace_name: z.string(),
    offer_kind: z.string(),
    trial_days: z.number().nullish(),
    plan_name: z.string().nullish(),
    expires_at: z.string().nullish(),
    // CIQ-2219: the offer's email already has an account, so the landing sends
    // it to sign-in and then the claim step instead of the signup form.
    account_exists: z.boolean().optional(),
});
export type OfferPreviewResponse = z.infer<typeof OfferPreviewResponseSchema>;

// GET /api/ddr/app-invites/{id} -- the unauthenticated signup landing behind an
// emailed app invite. The email is display only and LOCKED on the form; access
// comes from claiming the invite with that verified email.
export const AppInvitePreviewResponseSchema = z.object({
    email: z.string(),
    app_name: z.string(),
    account_exists: z.boolean(),
});
export type AppInvitePreviewResponse = z.infer<typeof AppInvitePreviewResponseSchema>;

/** Where onboarding finishes for someone who arrived from an app invite: the
 *  app-reauth bounce back to the app. Anything else is ignored, so a crafted
 *  `next` cannot redirect elsewhere. */
export function onboardingReturnUrl(raw: string | null | undefined): string | null {
    return raw?.startsWith('/app-reauth?') ? raw : null;
}

export const SetupIntentResponseSchema = z.object({
    stripe_customer_id: z.string(),
    client_secret: z.string(),
});
export type SetupIntentResponse = z.infer<typeof SetupIntentResponseSchema>;

export const ValidateStep1ResponseSchema = z.object({
    ok: z.boolean(),
});
export type ValidateStep1Response = z.infer<typeof ValidateStep1ResponseSchema>;

// `tokens`: the session that NAMES the workspace this call just created.
// The access token is a fat JWT carrying current_workspace_id, and it is minted
// at /verify_email, which for a corporate signup runs BEFORE any workspace
// exists. Without adopting this, the browser keeps claiming ws_id: null and
// every workspace-scoped call resolves a workspace that is not there (measured
// 2026-08-05: /wallet/can-send answered `depleted` on a live funded trial).
// Nullable because the backend degrades rather than failing a request whose
// workspace was already created; the client then falls back to /auth/refresh.
export const CompleteOnboardingResponseSchema = z.object({
    workspace_id: z.number(),
    workspace_name: z.string(),
    balance: z.number(),
    tokens: LoginResponseSchema.nullable().optional(),
});
export type CompleteOnboardingResponse = z.infer<typeof CompleteOnboardingResponseSchema>;

// POST /onboarding/corporate/complete-provisioned (CIQ-1591). Same session
// contract as /complete. `terms_attached: false` means the workspace exists but
// the trial/plan attach failed server-side — Sales repairs it from the console,
// so the client proceeds either way.
export const CompleteProvisionedResponseSchema = CompleteOnboardingResponseSchema.extend({
    offer_kind: z.string().optional(),
    terms_attached: z.boolean().optional(),
    paid_usd: z.number().nullish(),
});
export type CompleteProvisionedResponse = z.infer<typeof CompleteProvisionedResponseSchema>;

export const JoinExistingResponseSchema = z.object({
    workspace_id: z.number(),
    workspace_name: z.string(),
    // Joining switches current_workspace_id too, so it carries the same session.
    tokens: LoginResponseSchema.nullable().optional(),
});
export type JoinExistingResponse = z.infer<typeof JoinExistingResponseSchema>;

// POST /onboarding/corporate/skip-corporate (CIQ-2522 personal branch). Same
// session contract as /complete -- whichever outcome runs (domain-match join,
// or a fresh personal workspace) switches current_workspace_id, so the token
// that named none is wrong either way.
export const SkipCorporateResponseSchema = z.object({
    workspace_id: z.number(),
    workspace_type: z.enum(['personal', 'corporate']),
    tokens: LoginResponseSchema.nullable().optional(),
});
export type SkipCorporateResponse = z.infer<typeof SkipCorporateResponseSchema>;

// Validation error envelope returned by /onboarding/corporate/validate on 400
export const ValidationErrorBodySchema = z.object({
    detail: z.object({
        errors: z.record(z.string(), z.string()),
    }),
});
export type ValidationErrorBody = z.infer<typeof ValidationErrorBodySchema>;

// Storage key carrying the Company Name from corporate signup to the onboarding
// wizard. POST /create_user has no company-name field, so the value is stashed on
// submit and read once to prefill the workspace name at /onboarding/corporate.
//
// Lives in localStorage, not sessionStorage: the verification email link usually
// opens a new tab, where a per-tab value would already be gone. Because the value
// then outlives the tab that wrote it, it is bound to the email that typed it and
// only consumed for that account — an abandoned corporate signup must not prefill
// someone else's wizard run in the same browser.
export const SIGNUP_COMPANY_NAME_KEY = 'collectiviq.signup.companyName';

// Collected before the email is confirmed, spent by the setup wizard once it is.
// localStorage and email-bound for the same reasons as the key above.
export const WORKSPACE_SETUP_KEY = 'collectiviq.signup.workspaceSetup';

// Which account type this email signed up as. A corporate signup is already
// distinguishable by WORKSPACE_SETUP_KEY's presence, but a personal signup
// writes no handoff at all, so there was nothing else to tell /onboarding/setup
// apart from a corporate one once the one-shot `?intent=` query param is
// dropped -- auth.guard.ts and redirect-authenticated.guard.ts both build a
// bare `createUrlTree(['/onboarding/setup'])` with no query params, so any
// re-entry that is not the immediate post-verify chain (closing the tab and
// signing back in, landing on `/`) loses it. localStorage and email-bound for
// the same reasons as the keys above.
export const SIGNUP_INTENT_KEY = 'collectiviq.signup.intent';

export const SignupIntentHandoffSchema = z.object({
    email: z.string(),
    intent: SignupIntentSchema,
});
export type SignupIntentHandoff = z.infer<typeof SignupIntentHandoffSchema>;

export const WorkspaceSetupHandoffSchema = z.object({
    email: z.string(),
    workspaceName: z.string(),
    authorizedDomains: z.array(z.string()),
    // Carries the claim screen's own `?additional=1` through to the wizard's
    // finish -- an already-onboarded user creating another corporate
    // workspace (CIQ-634's JOIN_EXISTING / _guard_create_flow). Optional so a
    // handoff written before this field existed still parses.
    additional: z.boolean().optional(),
});
export type WorkspaceSetupHandoff = z.infer<typeof WorkspaceSetupHandoffSchema>;

// Where a user who already has a workspace claims a sales-provisioned offer
// (CIQ-2219): the additional-workspace wizard, which /status turns into the
// claim step when a live offer matches their verified email.
export const OFFER_CLAIM_URL = '/onboarding/corporate?additional=1';

export const SignupCompanyHandoffSchema = z.object({
    email: z.string(),
    companyName: z.string(),
});
export type SignupCompanyHandoff = z.infer<typeof SignupCompanyHandoffSchema>;

/** The stashed company name, but only for the account that typed it.
 *
 * The handoff outlives the tab that wrote it, so the email binding is what
 * stops an abandoned corporate signup prefilling the next account's run in the
 * same browser. Returns '' for a missing, malformed or foreign handoff, so a
 * caller can use it directly as a prefill value.
 *
 * Reading does not consume: `/onboarding/corporate` still needs the same
 * handoff to prefill the workspace name.
 */
export const signupCompanyNameFor = (stored: unknown, accountEmail: string | null | undefined): string => {
    const handoff = SignupCompanyHandoffSchema.safeParse(stored);
    if (!handoff.success) {
        return '';
    }
    const current = (accountEmail ?? '').trim().toLowerCase();
    return current && handoff.data.email.trim().toLowerCase() === current ? handoff.data.companyName : '';
};

/** What `complete-provisioned` reports it actually charged. */
export const CompleteProvisionedPaidSchema = z.object({ paid_usd: z.number().nullish() });

// Personal-ISP / free-mail domains. Mirrors backend security.PERSONAL_ISP_DOMAINS.
// Used to reject a personal-ISP email on the signup form's Corporate tab and in
// the corporate onboarding wizard (defense in depth - backend also blocks).
export const PERSONAL_ISP_DOMAINS: ReadonlySet<string> = new Set([
    // Free-mail / webmail providers
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'ymail.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'msn.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'aim.com',
    'protonmail.com',
    'proton.me',
    'gmx.com',
    'gmx.us',
    'mail.com',
    'zoho.com',
    'fastmail.com',
    'yandex.com',
    'yandex.ru',
    'mail.ru',
    'qq.com',
    '163.com',
    '126.com',
    'sina.com',
    // US residential / ISP-issued consumer mail
    'comcast.net',
    'xfinity.com',
    'att.net',
    'sbcglobal.net',
    'bellsouth.net',
    'ameritech.net',
    'pacbell.net',
    'swbell.net',
    'verizon.net',
    'frontiernet.net',
    'frontier.com',
    'charter.net',
    'spectrum.net',
    'rr.com',
    'twc.com',
    'roadrunner.com',
    'cox.net',
    'centurylink.net',
    'qwest.net',
    'earthlink.net',
    'optonline.net',
    'cablevision.net',
    'rcn.com',
    'windstream.net',
    'juno.com',
    'netzero.net',
]);

export function extractEmailDomain(email: string): string {
    const trimmed = (email || '').trim().toLowerCase();
    const at = trimmed.indexOf('@');
    return at >= 0 ? trimmed.slice(at + 1) : '';
}

export function isPersonalIspEmail(email: string): boolean {
    return PERSONAL_ISP_DOMAINS.has(extractEmailDomain(email));
}

// Mirrored by MIN_SIGNUP_AGE_YEARS in backend/presentation/api/v1/identity.py.
// The backend is the enforcing copy; this one only keeps the user from
// submitting a signup the server would reject.
export const MIN_SIGNUP_AGE_YEARS = 18;

export const DOB_REQUIRED_MESSAGE = 'Date of birth is required';
export const DOB_INVALID_MESSAGE = 'Enter a valid date of birth';
export const DOB_UNDERAGE_MESSAGE = 'You do not meet the minimum age requirement to use CollectivIQ.';

export type DateOfBirthParts = {
    month: string;
    day: string;
    year: string;
};

export type DateOfBirthError = {
    kind: 'dob-required' | 'dob-invalid' | 'dob-underage';
    message: string;
};

export function daysInMonth(month: number, year: number): number {
    if (!month) {
        return 31;
    }
    // Day 0 of the next month is the last day of this one, which gets leap
    // years right without a separate February branch.
    return new Date(year || 2000, month, 0).getDate();
}

export function completedYearsBetween(birth: Date, on: Date): number {
    let years = on.getFullYear() - birth.getFullYear();
    const monthDelta = on.getMonth() - birth.getMonth();
    if (monthDelta < 0 || (monthDelta === 0 && on.getDate() < birth.getDate())) {
        years -= 1;
    }
    return years;
}

/** Null when the three parts name a real date at least MIN_SIGNUP_AGE_YEARS ago. */
export function validateDateOfBirth(parts: DateOfBirthParts, on: Date): DateOfBirthError | null {
    const month = Number(parts.month);
    const day = Number(parts.day);
    const year = Number(parts.year);

    if (!parts.month || !parts.day || !parts.year) {
        return { kind: 'dob-required', message: DOB_REQUIRED_MESSAGE };
    }

    // Rejects 31 February, which the selects allow while the month is unset.
    if (!Number.isInteger(month) || !Number.isInteger(day) || !Number.isInteger(year) || day > daysInMonth(month, year)) {
        return { kind: 'dob-invalid', message: DOB_INVALID_MESSAGE };
    }

    const birth = new Date(year, month - 1, day);
    if (birth.getTime() > on.getTime()) {
        return { kind: 'dob-invalid', message: DOB_INVALID_MESSAGE };
    }

    if (completedYearsBetween(birth, on) < MIN_SIGNUP_AGE_YEARS) {
        return { kind: 'dob-underage', message: DOB_UNDERAGE_MESSAGE };
    }

    return null;
}

/** ISO-8601 date for the wire, built from the parts rather than Date#toISOString,
 *  which would shift the day for anyone west of UTC. */
export function dateOfBirthToIso(parts: DateOfBirthParts): string {
    return `${parts.year.padStart(4, '0')}-${parts.month.padStart(2, '0')}-${parts.day.padStart(2, '0')}`;
}
